import React, { useEffect, useId, useRef, useState } from "react";
import DrawerA11yWrapper from "../../ui/DrawerA11yWrapper";
import Button from "../../ui/Button";
import { colors, spacing } from "../../ui/tokens";
import { GLYDER_NAV_LOGO_URL } from "../../brandAssets";
import { summariseMyOverview } from "../../api/george.api";

function Summary({ text }) {
  return text.split("\n\n").map((paragraph, index) => {
    const lines = paragraph.split("\n");
    const prose = lines.filter((line) => !line.startsWith("- "));
    const bullets = lines.filter((line) => line.startsWith("- "));
    return <div key={index}>
      {prose.length > 0 && <p>{prose.join(" ")}</p>}
      {bullets.length > 0 && <ul>{bullets.map((line, i) => <li key={i}>{line.slice(2)}</li>)}</ul>}
    </div>;
  });
}

export default function GeorgeOverviewDrawer({ onClose }) {
  const titleId = useId();
  const descriptionId = useId();
  const pending = useRef(null);
  const [state, setState] = useState({ status: "idle", summary: "" });
  useEffect(() => () => {
    pending.current?.abort();
    pending.current = null;
  }, []);

  async function run(task) {
    pending.current?.abort();
    const controller = new AbortController();
    pending.current = controller;
    setState({ status: "loading", summary: "" });
    try {
      const summary = await summariseMyOverview(task, { signal: controller.signal });
      if (!controller.signal.aborted && pending.current === controller) {
        setState({ status: "success", summary });
      }
    } catch (_error) {
      if (!controller.signal.aborted && pending.current === controller) {
        setState({ status: "error", summary: "" });
      }
    }
  }

  return <DrawerA11yWrapper open onClose={onClose} labelledBy={titleId} describedBy={descriptionId}
    width={480} containerStyle={{ width: "min(480px, 100vw)", minWidth: 0, boxSizing: "border-box",
      padding: spacing.xl, color: colors.text, overflowWrap: "anywhere" }}>
    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: spacing.md }}>
      <div style={{ display: "flex", alignItems: "center", gap: spacing.sm }}>
        {/* Temporary existing Glyder brand mark; not a final George mascot. */}
        <img src={GLYDER_NAV_LOGO_URL} alt="" style={{ width: 40, height: 40, objectFit: "contain" }} />
        <h2 id={titleId} style={{ margin: 0 }}>George</h2>
      </div>
      <Button type="button" variant="ghost" onClick={onClose} aria-label="Close George">Close</Button>
    </div>
    <p id={descriptionId} style={{ color: colors.textMuted }}>Looking at: My Overview</p>
    <p>Only your authorised pinned My Overview items are included.</p>
    <div style={{ display: "grid", gap: spacing.sm }}>
      <Button type="button" disabled={state.status === "loading"} onClick={() => run("summary")}>
        Summarise this page
      </Button>
      <Button type="button" variant="secondary" disabled={state.status === "loading"} onClick={() => run("attention")}>
        What needs my attention?
      </Button>
    </div>
    <div aria-live="polite" aria-busy={state.status === "loading"} style={{ lineHeight: 1.6 }}>
      {state.status === "loading" && <p role="status">George is reviewing your pinned items…</p>}
      {state.status === "success" && <Summary text={state.summary} />}
    </div>
    {state.status === "error" && <p role="alert">George is temporarily unavailable. Your Glyder data is unaffected.</p>}
  </DrawerA11yWrapper>;
}
